<template>
  <LayoutBasic :showLinksMenu="false" :routeBack="rutaVolver" :routeBackLabel="etiquetaVolver" mobileTitle="Cuentas corrientes">
    <template v-slot:navigation-title>Cuentas corrientes</template>

    <template v-slot:navigation-content>
      <v-text-field
        rounded
        outlined
        style="background-color: #3a3b3c"
        dense
        v-model="search"
        prepend-inner-icon="mdi-magnify"
        placeholder="Buscar cliente"
        single-line
        hide-details
        clearable
      ></v-text-field>

      <div class="cc-filtro-titulo">Estado</div>
      <v-list nav dense class="pa-0">
        <v-list-item
          v-for="opcion in estados"
          :key="opcion.value"
          @click="setEstado(opcion.value)"
          :class="['cc-filtro-item', { 'cc-filtro-item--activo': estado === opcion.value }]"
        >
          <v-list-item-icon class="mr-3">
            <v-icon small>{{ opcion.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ opcion.text }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <div class="cc-filtro-titulo">Ordenar por</div>
      <v-select
        v-model="orden"
        :items="ordenes"
        dense
        outlined
        hide-details
        style="background-color: #3a3b3c"
      ></v-select>
    </template>

    <template v-slot:vista-previa>
      <div class="cc-root">
        <!-- Encabezado -->
        <div class="cc-topbar">
          <div>
            <div class="cc-topbar__title white--text">Deuda de clientes</div>
            <div class="cc-topbar__sub">
              Saldo pendiente de facturas y notas de venta ·
              {{ resumen.cuentas_habilitadas }} de {{ resumen.cuentas_totales }} cuentas habilitadas
            </div>
          </div>
          <div class="cc-topbar__filters">
            <SucursalesVFilter />
            <v-btn small text color="grey" :loading="loading" @click="fetch">
              <v-icon left small>mdi-refresh</v-icon> Actualizar
            </v-btn>
          </div>
        </div>

        <template v-if="loading && !cargadoAlgunaVez">
          <div class="cc-kpi-row">
            <v-skeleton-loader v-for="n in 4" :key="n" dark type="card" height="96" />
          </div>
          <v-skeleton-loader dark type="table" class="mt-4" />
        </template>

        <template v-else>
          <!-- KPIs -->
          <div class="cc-kpi-row">
            <div class="cc-kpi cc-kpi--total">
              <div class="cc-kpi__label">Deuda total</div>
              <div class="cc-kpi__value">{{ resumen.deuda_total | convertPesoArg }}</div>
              <div class="cc-kpi__meta">
                {{ resumen.clientes_con_deuda }} clientes con saldo
              </div>
            </div>

            <div class="cc-kpi cc-kpi--vencida">
              <div class="cc-kpi__label">Vencida</div>
              <div class="cc-kpi__value cc-kpi__value--alerta">
                {{ resumen.deuda_vencida | convertPesoArg }}
              </div>
              <div class="cc-kpi__meta">
                {{ pct(resumen.deuda_vencida, resumen.deuda_total) }}% del total ·
                {{ resumen.clientes_con_deuda_vencida }} clientes
              </div>
            </div>

            <div class="cc-kpi cc-kpi--porvencer">
              <div class="cc-kpi__label">Por vencer</div>
              <div class="cc-kpi__value">{{ resumen.deuda_por_vencer | convertPesoArg }}</div>
              <div class="cc-kpi__meta">Todavía dentro del plazo pactado</div>
            </div>

            <div class="cc-kpi cc-kpi--promedio">
              <div class="cc-kpi__label">Deuda promedio</div>
              <div class="cc-kpi__value">{{ resumen.deuda_promedio | convertPesoArg }}</div>
              <div class="cc-kpi__meta">Por cliente con saldo</div>
            </div>
          </div>

          <!-- Alertas accionables -->
          <div class="cc-alertas" v-if="hayAlertas">
            <v-chip
              v-if="resumen.clientes_excedidos > 0"
              small
              outlined
              color="deep-orange lighten-2"
              class="mr-2"
              @click="setEstado('excedidos')"
            >
              <v-icon left x-small>mdi-alert-octagon-outline</v-icon>
              {{ resumen.clientes_excedidos }} pasaron su límite de crédito
            </v-chip>

            <v-chip
              v-if="resumen.clientes_sin_cuenta_corriente > 0"
              small
              outlined
              color="amber lighten-2"
              class="mr-2"
              @click="setEstado('sin_cuenta')"
            >
              <v-icon left x-small>mdi-account-question-outline</v-icon>
              {{ resumen.clientes_sin_cuenta_corriente }} deben sin tener cuenta corriente
            </v-chip>

            <v-chip
              v-if="Number(resumen.credito_a_favor) > 0"
              small
              outlined
              color="teal lighten-2"
            >
              <v-icon left x-small>mdi-cash-refund</v-icon>
              {{ resumen.credito_a_favor | convertPesoArg }} de anticipos sin imputar
            </v-chip>
          </div>

          <!-- Antigüedad de la deuda vencida -->
          <div class="cc-panel mb-4">
            <div class="cc-panel__head">
              <v-icon small class="mr-2" color="rgba(255,255,255,0.5)">mdi-clock-alert-outline</v-icon>
              <span>Antigüedad de la deuda vencida</span>
            </div>
            <div class="cc-panel__body">
              <div v-if="!Number(resumen.deuda_vencida)" class="cc-empty">
                No hay deuda vencida. 👌
              </div>
              <div v-else class="cc-aging">
                <div v-for="tramo in resumen.antiguedad" :key="tramo.rango" class="cc-aging__item">
                  <div class="cc-aging__rango">{{ tramo.rango }} días</div>
                  <div class="cc-aging__monto">{{ tramo.monto | convertPesoArg }}</div>
                  <div class="cc-bar">
                    <div
                      class="cc-bar__fill"
                      :class="'cc-bar__fill--' + claseTramo(tramo.rango)"
                      :style="{ width: pct(tramo.monto, resumen.deuda_vencida) + '%' }"
                    />
                  </div>
                  <div class="cc-aging__pct">
                    {{ pct(tramo.monto, resumen.deuda_vencida) }}%
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Listado -->
          <div class="cc-panel">
            <div class="cc-panel__head">
              <v-icon small class="mr-2" color="rgba(255,255,255,0.5)">mdi-account-cash-outline</v-icon>
              <span>{{ tituloListado }}</span>
              <v-spacer></v-spacer>
              <span class="cc-panel__count">{{ total }}</span>
            </div>

            <div v-if="!clientes.length" class="cc-empty">
              No hay clientes para este filtro.
            </div>

            <div v-else class="cc-tabla-wrap">
              <table class="cc-tabla">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th class="text-right">Deuda</th>
                    <th class="text-right">Vencida</th>
                    <th class="text-right">Mora</th>
                    <th class="text-right">Disponible</th>
                    <th class="text-right">Último pago</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="cliente in clientes"
                    :key="cliente.id"
                    @click="verCuentaCorriente(cliente)"
                  >
                    <td>
                      <div class="cc-cliente">
                        <span class="cc-estado-dot" :class="'cc-estado-dot--' + cliente.estado"></span>
                        <div class="cc-cliente__datos">
                          <span class="cc-cliente__nombre">{{ cliente.nombre }}</span>
                          <span class="cc-cliente__meta">
                            <template v-if="cliente.comprobantes_pendientes">
                              {{ cliente.comprobantes_pendientes }} comprobante(s) sin cobrar
                            </template>
                            <template v-else>Sin saldo pendiente</template>
                            <template v-if="!cliente.tiene_cuenta_corriente"> · sin cuenta corriente</template>
                            <template v-else-if="!cliente.cuenta_habilitada"> · cuenta deshabilitada</template>
                          </span>
                        </div>
                      </div>
                    </td>
                    <td class="text-right cc-monto">{{ cliente.deuda_total | convertPesoArg }}</td>
                    <td class="text-right cc-monto" :class="{ 'cc-monto--alerta': Number(cliente.deuda_vencida) > 0 }">
                      {{ cliente.deuda_vencida | convertPesoArg }}
                    </td>
                    <td class="text-right">
                      <span v-if="cliente.dias_mora" class="cc-mora" :class="claseMora(cliente.dias_mora)">
                        {{ cliente.dias_mora }} d
                      </span>
                      <span v-else class="cc-dim">—</span>
                    </td>
                    <td class="text-right">
                      <span v-if="cliente.disponible === null" class="cc-dim">Sin límite</span>
                      <span v-else :class="{ 'cc-monto--alerta': Number(cliente.disponible) < 0 }">
                        {{ cliente.disponible | convertPesoArg }}
                      </span>
                    </td>
                    <td class="text-right">
                      <span v-if="cliente.ultimo_pago">
                        {{ fecha(cliente.ultimo_pago) }}
                        <span class="cc-dim d-block">hace {{ cliente.dias_sin_pagar }} d</span>
                      </span>
                      <span v-else class="cc-dim">Nunca</span>
                    </td>
                    <td class="text-right">
                      <v-icon small color="rgba(255,255,255,0.25)">mdi-chevron-right</v-icon>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="cc-paginacion" v-if="paginas > 1">
              <v-pagination
                v-model="page"
                :length="paginas"
                :total-visible="7"
                dark
              ></v-pagination>
            </div>
          </div>
        </template>
      </div>
    </template>
  </LayoutBasic>
</template>

<script>
import LayoutBasic from "@/layouts/LayoutBasic.vue";
import SucursalesVFilter from "@/components/sucursal/SucursalesVFilter.vue";
import cuentaCorrienteAPI from "@/api/clientes/cuentacorriente";

const RESUMEN_VACIO = {
  deuda_total: 0,
  deuda_vencida: 0,
  deuda_por_vencer: 0,
  credito_a_favor: 0,
  clientes_con_deuda: 0,
  clientes_con_deuda_vencida: 0,
  clientes_excedidos: 0,
  clientes_sin_cuenta_corriente: 0,
  cuentas_totales: 0,
  cuentas_habilitadas: 0,
  deuda_promedio: 0,
  antiguedad: [],
};

export default {
  name: "ClientesCuentasCorrientes",
  components: { LayoutBasic, SucursalesVFilter },
  // Se llega desde Clientes o desde Reportes: "Volver" tiene que devolver al
  // lugar de origen, no siempre a la lista de clientes.
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.origen = from && from.name ? { name: from.name, path: from.path } : null;
    });
  },
  data() {
    return {
      origen: null,
      loading: false,
      cargadoAlgunaVez: false,
      search: "",
      estado: "con_deuda",
      orden: "deuda",
      page: 1,
      pageSize: 20,
      total: 0,
      clientes: [],
      resumen: { ...RESUMEN_VACIO },
      estados: [
        { value: "con_deuda", text: "Con deuda", icon: "mdi-cash-clock" },
        { value: "vencidos", text: "Vencidos", icon: "mdi-alert-circle-outline" },
        { value: "excedidos", text: "Pasaron el límite", icon: "mdi-alert-octagon-outline" },
        { value: "sin_cuenta", text: "Deben sin cuenta", icon: "mdi-account-question-outline" },
        { value: "al_dia", text: "Al día", icon: "mdi-check-circle-outline" },
        { value: "todos", text: "Todos", icon: "mdi-account-group-outline" },
      ],
      ordenes: [
        { value: "deuda", text: "Mayor deuda" },
        { value: "vencido", text: "Mayor deuda vencida" },
        { value: "mora", text: "Más días de mora" },
        { value: "ultimo_pago", text: "Hace más que no paga" },
        { value: "nombre", text: "Nombre" },
      ],
      debounce: null,
    };
  },
  computed: {
    rutaVolver() {
      return this.origen ? -1 : { name: "empresa.clientes" };
    },
    etiquetaVolver() {
      if (this.origen && this.origen.path.startsWith("/reportes")) return "Reportes";
      return "Clientes";
    },
    paginas() {
      return Math.ceil(this.total / this.pageSize) || 1;
    },
    hayAlertas() {
      return (
        this.resumen.clientes_excedidos > 0 ||
        this.resumen.clientes_sin_cuenta_corriente > 0 ||
        Number(this.resumen.credito_a_favor) > 0
      );
    },
    tituloListado() {
      const opcion = this.estados.find((e) => e.value === this.estado);
      return opcion ? opcion.text : "Clientes";
    },
  },
  methods: {
    pct(valor, total) {
      if (!Number(total)) return 0;
      return Math.round((Number(valor) / Number(total)) * 100);
    },
    claseTramo(rango) {
      return { "1-30": "leve", "31-60": "medio", "61-90": "alto", "+90": "critico" }[rango] || "leve";
    },
    claseMora(dias) {
      if (dias > 90) return "cc-mora--critico";
      if (dias > 30) return "cc-mora--alto";
      return "cc-mora--leve";
    },
    setEstado(valor) {
      this.estado = valor;
      this.page = 1;
    },
    verCuentaCorriente(cliente) {
      this.$router.push({
        name: "empresa.cliente.cuentacorriente",
        params: { id_cliente: cliente.id },
      });
    },
    fecha(valor) {
      return valor ? this.moment(valor).format("DD/MM/YY") : "";
    },
    fetch() {
      this.loading = true;
      cuentaCorrienteAPI
        .reporte({
          estado: this.estado,
          orden: this.orden,
          search: this.search || undefined,
          sucursal: this.$route.query.uuid_sucursal,
          page: this.page,
          page_size: this.pageSize,
        })
        .then(({ data }) => {
          this.clientes = data.results || [];
          this.total = data.count || 0;
          this.resumen = { ...RESUMEN_VACIO, ...(data.resumen || {}) };
        })
        .catch(() => {
          this.clientes = [];
          this.total = 0;
          this.resumen = { ...RESUMEN_VACIO };
        })
        .finally(() => {
          this.loading = false;
          this.cargadoAlgunaVez = true;
        });
    },
    fetchConDebounce() {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(this.fetch, 350);
    },
  },
  created() {
    this.fetch();
  },
  watch: {
    estado() { this.fetch(); },
    orden() { this.page = 1; this.fetch(); },
    page() { this.fetch(); },
    search() { this.page = 1; this.fetchConDebounce(); },
    "$route.query.uuid_sucursal"() { this.page = 1; this.fetch(); },
  },
  beforeDestroy() {
    clearTimeout(this.debounce);
  },
};
</script>

<style scoped>
.cc-root { padding: 4px 0; }

/* ── Filtros del drawer ─ */
.cc-filtro-titulo {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.35);
  margin: 20px 0 8px;
}
.cc-filtro-item { border-radius: 8px !important; }
.cc-filtro-item--activo {
  background: rgba(66, 165, 245, 0.12) !important;
  color: #90caf9 !important;
}
.cc-filtro-item--activo .v-icon { color: #90caf9 !important; }

/* ── Topbar ─ */
.cc-topbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
}
.cc-topbar__title { font-size: 18px; font-weight: 700; line-height: 1.3; }
.cc-topbar__sub { font-size: 12px; color: rgba(255,255,255,0.4); margin-top: 3px; }
.cc-topbar__filters { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }

/* ── KPIs ─ */
.cc-kpi-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}
.cc-kpi {
  background: #272727;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 8px;
  padding: 18px 20px;
  border-top: 3px solid transparent;
}
.cc-kpi--total     { border-top-color: #42a5f5; }
.cc-kpi--vencida   { border-top-color: #ef5350; }
.cc-kpi--porvencer { border-top-color: #ffa726; }
.cc-kpi--promedio  { border-top-color: #26a69a; }

.cc-kpi__label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  font-weight: 600;
  color: rgba(255,255,255,0.4);
  margin-bottom: 10px;
}
.cc-kpi__value {
  font-size: 22px;
  font-weight: 700;
  color: rgba(255,255,255,0.87);
  line-height: 1.2;
  margin-bottom: 10px;
}
.cc-kpi__value--alerta { color: #ef9a9a; }
.cc-kpi__meta { font-size: 11px; color: rgba(255,255,255,0.35); }

/* ── Alertas ─ */
.cc-alertas { margin-bottom: 16px; }
.cc-alertas .v-chip { cursor: pointer; }

/* ── Panel ─ */
.cc-panel {
  background: #272727;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 8px;
  overflow: hidden;
}
.cc-panel__head {
  display: flex;
  align-items: center;
  padding: 11px 16px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255,255,255,0.4);
  border-bottom: 1px solid rgba(255,255,255,0.07);
  background: rgba(255,255,255,0.03);
}
.cc-panel__count { color: rgba(255,255,255,0.6); }
.cc-panel__body { padding: 8px 0; }

/* ── Antigüedad ─ */
.cc-aging { padding: 8px 16px; }
.cc-aging__item {
  display: grid;
  grid-template-columns: 90px 140px 1fr 48px;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
}
.cc-aging__rango { font-size: 12px; color: rgba(255,255,255,0.6); }
.cc-aging__monto { font-size: 13px; font-weight: 600; color: rgba(255,255,255,0.87); }
.cc-aging__pct { font-size: 11px; color: rgba(255,255,255,0.3); text-align: right; }

.cc-bar { height: 6px; background: rgba(255,255,255,0.07); border-radius: 3px; overflow: hidden; }
.cc-bar__fill { height: 100%; border-radius: 3px; transition: width 0.5s cubic-bezier(0.4,0,0.2,1); }
.cc-bar__fill--leve    { background: #ffd54f; }
.cc-bar__fill--medio   { background: #ffa726; }
.cc-bar__fill--alto    { background: #ff7043; }
.cc-bar__fill--critico { background: #ef5350; }

/* ── Tabla ─ */
.cc-tabla-wrap { overflow-x: auto; }
.cc-tabla { width: 100%; border-collapse: collapse; }
.cc-tabla th {
  text-align: left;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255,255,255,0.35);
  font-weight: 600;
  padding: 10px 16px;
  white-space: nowrap;
  border-bottom: 1px solid rgba(255,255,255,0.07);
}
.cc-tabla td {
  padding: 10px 16px;
  font-size: 13px;
  color: rgba(255,255,255,0.75);
  border-bottom: 1px solid rgba(255,255,255,0.05);
  white-space: nowrap;
}
.cc-tabla tbody tr { cursor: pointer; }
.cc-tabla tbody tr:hover { background: rgba(255,255,255,0.03); }
.cc-tabla tbody tr:last-child td { border-bottom: none; }

.cc-cliente { display: flex; align-items: center; gap: 10px; }
.cc-cliente__datos { display: flex; flex-direction: column; }
.cc-cliente__nombre { color: rgba(255,255,255,0.87); font-weight: 500; }
.cc-cliente__meta { font-size: 11px; color: rgba(255,255,255,0.3); }

.cc-estado-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.cc-estado-dot--SIN_DEUDA { background: #4caf50; }
.cc-estado-dot--AL_DIA    { background: #42a5f5; }
.cc-estado-dot--VENCIDO   { background: #ffa726; }
.cc-estado-dot--EXCEDIDO  { background: #ef5350; }

.cc-monto { color: rgba(255,255,255,0.87); font-weight: 600; }
.cc-monto--alerta { color: #ef9a9a; }
.cc-dim { color: rgba(255,255,255,0.25); font-size: 11px; }

.cc-mora {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
}
.cc-mora--leve    { background: rgba(255,213,79,0.15);  color: #ffd54f; }
.cc-mora--alto    { background: rgba(255,112,67,0.15);  color: #ff8a65; }
.cc-mora--critico { background: rgba(239,83,80,0.18);   color: #ef9a9a; }

.cc-empty {
  padding: 28px 16px;
  text-align: center;
  font-size: 12px;
  color: rgba(255,255,255,0.25);
}

.cc-paginacion { padding: 12px 0; }

@media (max-width: 960px) {
  .cc-kpi-row { grid-template-columns: repeat(2, 1fr); }
  .cc-aging__item { grid-template-columns: 70px 1fr 40px; }
  .cc-aging__monto { grid-column: 2; }
  .cc-bar { grid-column: 1 / -1; }
}
</style>
